import { useParams, Link } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { TerminlinkDialog } from "@/components/portal/TerminlinkDialog"
import {
  ExternalLink, Mail, Calendar, FileText, User, MapPin, TriangleAlert,
  Phone, AtSign, Briefcase, Wrench, BookOpen,
} from "lucide-react"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { ZurueckZurSuche } from "@/components/search/ZurueckZurSuche"

/**
 * /projekte/:id — Projekt-Detail-Ansicht.
 *
 * Phase A (11.05.2026): read-only. Holt alles aus `/api/v1/projects/{id}/detail`:
 * - Kontakt + Adresse
 * - Status/Step + Pipeline
 * - Gewerk + Mitarbeiter
 * - Termine
 * - Dokumente (Angebote/Rechnungen) mit HERO-Direktlink
 * - Zugeordnete E-Mails (Click → /emails/{id})
 */

type ProjectDetail = {
  ok: boolean
  /** true, wenn HERO nicht erreichbar war und nur der lokale Bestand kommt. */
  partial?: boolean
  partial_reason?: string
  /** Ticket #21: Belege, die am Kontakt haengen statt an einem Vorgang.
   *  Sie werden hier NICHT gelistet — nur gezaehlt, mit Weg zum Kontakt. */
  belege_am_kontakt?: {
    anzahl: number
    contact_id: number | null
    neuester?: string | null
  }
  project: {
    id: number
    title: string
    raw_title: string | null
    name: string | null
    project_nr: string | null
    type_id: number | null
    measure_id: number | null
    measure_name: string | null
    partner_notes: string
    contact: {
      id: number | null
      salutation: string
      first_name: string
      last_name: string
      email: string
      phone: string
      company_name: string
      address: { street: string; city: string; zipcode: string }
    }
    status: {
      id: number | null
      step_id: number | null
      step_name: string
      status_code: number | null
      group_name: string
    }
    partner: { id: number | null; name: string }
  }
  events: Array<{
    id: number; title: string; description: string | null
    start: string; end: string; all_day: boolean
    deleted: boolean; is_done: boolean
    category: string
    partners: Array<{ id: number; name: string }>
  }>
  documents: Array<{
    id: number; nr: string; type: string; type_name: string
    created: string | null; modified: string | null
    link_view: string | null; status_name: string
  }>
  emails: Array<{
    id: number; subject: string | null
    sender: string | null; sender_email: string | null
    received_at: string | null
    status: string; category: string | null
    direction: string | null
  }>
  histories: Array<{
    id: number; title: string; text: string
    type_code: number | null; author: string; created: string | null
  }>
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    })
  } catch { return iso }
}

// HERO liefert link_view als relativen Pfad ("/partner/Documents/view/123").
// Wir prefixen mit der HERO-Web-Base damit der Link tatsaechlich zur HERO-UI
// fuehrt (oeffnet PDF/Dokument im Browser; User muss in HERO eingeloggt sein).
const HERO_WEB_BASE = "https://login.hero-software.de"
function heroLink(linkView: string | null): string | null {
  if (!linkView) return null
  if (linkView.startsWith("http://") || linkView.startsWith("https://")) return linkView
  if (linkView.startsWith("/")) return HERO_WEB_BASE + linkView
  return `${HERO_WEB_BASE}/${linkView}`
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb",
      padding: 20, marginBottom: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        {icon}
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  )
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [tenantSlug] = useTenantFilter()
  const [terminlinkOffen, setTerminlinkOffen] = useState(false)

  const detail = useQuery<ProjectDetail>({
    queryKey: ["project-detail", id, tenantSlug],
    enabled: !!id,
    queryFn: async () => {
      const url = `/api/v1/projects/${id}/detail${tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ""}`
      const r = await fetch(url, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  if (detail.isLoading) {
    return <div style={{ padding: 40, textAlign: "center" }}>Lade Projekt…</div>
  }
  if (detail.isError || !detail.data) {
    return (
      <div style={{ padding: 40 }}>
        <ZurueckZurSuche ersatz={{ to: "/projekte", label: "Zur Suche" }} />
        <div style={{ color: "#dc2626", marginTop: 20 }}>
          Projekt nicht gefunden oder Fehler beim Laden.
        </div>
      </div>
    )
  }

  const { project: p, events, documents, emails, histories } = detail.data
  const amKontakt = detail.data.belege_am_kontakt
  const c = p.contact
  const s = p.status

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <ZurueckZurSuche ersatz={{ to: "/projekte", label: "Zur Suche" }} />

      {/* 🔴 Eine halbe Seite ist besser als „Fehler beim Laden" — aber sie
          muss sagen, dass sie halb ist. Sonst haelt man ein fehlendes
          Logbuch fuer „gibt es nicht". */}
      {detail.data.partial && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 9, background: "#fffbeb",
          border: "1px solid #fde68a", borderRadius: 10, padding: "10px 13px",
          fontSize: 12.5, color: "#92400e", marginBottom: 14, lineHeight: 1.5,
        }}>
          <TriangleAlert style={{ width: 15, height: 15, flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Nur lokaler Stand.</strong> {detail.data.partial_reason}.
            Stammdaten, Mails, Belege und Termine kommen aus unserem Bestand;
            das HERO-Logbuch und der Live-Status fehlen.
          </span>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          {p.project_nr && (
            <span style={{
              fontSize: 13, fontWeight: 700, color: "#475569",
              padding: "4px 10px", background: "#f1f5f9", borderRadius: 6,
            }}>{p.project_nr}</span>
          )}
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#1a1a2e" }}>
            {p.title}
          </h1>
          {/* 🔴 Interne ID nur bei einem echten HERO-Vorgang — bei einem
              Mandanten ohne HERO-Zugang waere „HERO #…" schlicht gelogen. */}
          {!detail.data.partial && (
            <span style={{ fontSize: 12, color: "#94a3b8" }}>HERO #{p.id}</span>
          )}
        </div>
        {s.step_name && (
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              fontSize: 12, color: "#1d4ed8", fontWeight: 600,
              padding: "3px 10px", background: "#dbeafe", borderRadius: 12,
            }}>
              {s.step_name}
            </span>
            {s.group_name && s.group_name !== s.step_name && (
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                (Gruppe: {s.group_name})
              </span>
            )}
          </div>
        )}
      </div>

      {/* 2-Spalten-Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Kontakt */}
        <Section title="Kontakt" icon={<User className="w-4 h-4" style={{ color: "#475569" }} />}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {c.salutation && <span>{c.salutation} </span>}
              {c.first_name} {c.last_name}
            </div>
            {c.company_name && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#475569" }}>
                <Briefcase className="w-3.5 h-3.5" />
                {c.company_name}
              </div>
            )}
            {c.email && (
              <a href={`mailto:${c.email}`} style={{ display: "flex", alignItems: "center", gap: 6, color: "#1d4ed8", textDecoration: "none" }}>
                <AtSign className="w-3.5 h-3.5" />
                {c.email}
              </a>
            )}
            {c.phone && (
              <a href={`tel:${c.phone}`} style={{ display: "flex", alignItems: "center", gap: 6, color: "#1d4ed8", textDecoration: "none" }}>
                <Phone className="w-3.5 h-3.5" />
                {c.phone}
              </a>
            )}
            {(c.address.street || c.address.city) && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 6, color: "#475569", marginTop: 4 }}>
                <MapPin className="w-3.5 h-3.5" style={{ marginTop: 3 }} />
                <div>
                  {c.address.street && <div>{c.address.street}</div>}
                  {(c.address.zipcode || c.address.city) && (
                    <div>{c.address.zipcode} {c.address.city}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* Projekt-Info */}
        <Section title="Projekt-Info" icon={<Wrench className="w-4 h-4" style={{ color: "#475569" }} />}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            <Row label="Gewerk" value={p.measure_name || `#${p.measure_id || "?"}`} />
            <Row label="Mitarbeiter" value={p.partner.name || "—"} />
            <Row label="Status-Step" value={s.step_name || "—"} />
            <Row label="Status-Code" value={s.status_code != null ? String(s.status_code) : "—"} />
            {p.partner_notes && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                  Notiz
                </div>
                <div style={{ color: "#475569", fontSize: 12, whiteSpace: "pre-wrap" }}>
                  {p.partner_notes}
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* Termine */}
        <Section title={`Termine (${events.length})`} icon={<Calendar className="w-4 h-4" style={{ color: "#475569" }} />}>
          {tenantSlug && (
            <div style={{ marginBottom: 8 }}>
              <button type="button" className="pp-btn" onClick={() => setTerminlinkOffen(true)} data-testid="terminlink-oeffnen">
                Terminvorschläge senden
              </button>
            </div>
          )}
          {terminlinkOffen && tenantSlug && (
            <TerminlinkDialog tenant={tenantSlug} projectId={p.id} onClose={() => setTerminlinkOffen(false)} />
          )}
          {events.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Keine Termine</div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
              {events.slice(0, 10).map((e) => (
                <li key={e.id} style={{
                  padding: "8px 0", borderBottom: "1px solid #f1f5f9",
                  opacity: e.is_done ? 0.6 : 1,
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{e.title || "(ohne Titel)"}</div>
                  <div style={{ color: "#64748b", marginTop: 2 }}>
                    {formatDate(e.start)} – {formatDate(e.end)}
                    {e.category && ` · ${e.category}`}
                  </div>
                  {e.partners.length > 0 && (
                    <div style={{ color: "#94a3b8", marginTop: 2 }}>
                      {e.partners.map(pp => pp.name).join(", ")}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Dokumente */}
        <Section title={`Dokumente (${documents.length})`} icon={<FileText className="w-4 h-4" style={{ color: "#475569" }} />}>
          {/* Ticket #21 — „Dokumente (0)" hiess bisher fuer den Leser „es gibt
              keine". Oft heisst es aber „sie haengen am Kontakt statt hier":
              bei Sanitec 227 Belege, darunter 100 versendete Rechnungen.

              🔴 Die Belege werden hier bewusst NICHT gelistet. Was nur am
              Kontakt haengt, gehoert auch nur dorthin — wuerden sie hier
              stehen, saehe die Zuordnung sauber aus und niemand haengt sie in
              HERO um. Gesagt wird nur, DASS es sie gibt, und wo sie stehen. */}
          {amKontakt && amKontakt.anzahl > 0 && amKontakt.contact_id && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
              padding: "9px 11px", marginBottom: documents.length ? 10 : 8, fontSize: 12,
              color: "#475569", lineHeight: 1.5,
            }}>
              <TriangleAlert className="w-3.5 h-3.5" style={{ color: "#94a3b8", flexShrink: 0, marginTop: 2 }} />
              <div>
                {amKontakt.anzahl === 1
                  ? "Ein weiterer Beleg liegt am Kontakt und ist keinem Vorgang zugeordnet."
                  : `${amKontakt.anzahl} weitere Belege liegen am Kontakt und sind keinem Vorgang zugeordnet.`}
                {" "}
                <Link
                  to={`/projekte?contact_id=${amKontakt.contact_id}${tenantSlug ? `&tenant=${encodeURIComponent(tenantSlug)}` : ""}`}
                  style={{ color: "#1d4ed8", textDecoration: "none", fontWeight: 600 }}
                >
                  Beim Kontakt ansehen
                </Link>
              </div>
            </div>
          )}
          {documents.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Keine Dokumente</div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
              {documents.slice(0, 15).map((d) => (
                <li key={d.id} style={{
                  padding: "8px 0", borderBottom: "1px solid #f1f5f9",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      {d.nr || `#${d.id}`} <span style={{ color: "#64748b", fontWeight: 400 }}>· {d.type_name}</span>
                    </div>
                    <div style={{ color: "#94a3b8", marginTop: 2 }}>
                      {formatDate(d.created)}
                      {d.status_name && ` · ${d.status_name}`}
                    </div>
                  </div>
                  {heroLink(d.link_view) && (
                    <a
                      href={heroLink(d.link_view) || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Oeffnet das Dokument in HERO (neuer Tab). Du musst in HERO eingeloggt sein."
                      style={{
                        color: "#1d4ed8", textDecoration: "none", display: "inline-flex",
                        alignItems: "center", gap: 4, fontSize: 12,
                      }}
                    >
                      In HERO öffnen <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* E-Mails */}
        <Section title={`E-Mails (${emails.length})`} icon={<Mail className="w-4 h-4" style={{ color: "#475569" }} />}>
          {emails.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Keine zugeordneten E-Mails</div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
              {emails.slice(0, 10).map((e) => (
                <li key={e.id} style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <Link to={`/emails/${e.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                    <div style={{ fontWeight: 600, color: "#1a1a2e" }}>
                      {e.subject || "(ohne Betreff)"}
                    </div>
                    <div style={{ color: "#64748b", marginTop: 2 }}>
                      {e.direction === "sent" ? "→ " : "← "}
                      {e.sender_email || e.sender || "?"} · {formatDate(e.received_at)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Logbuch (volle Breite, chronologischer Activity-Stream aus HERO) */}
      <div style={{ marginTop: 16 }}>
        <Section title={`Logbuch (${histories.length})`} icon={<BookOpen className="w-4 h-4" style={{ color: "#475569" }} />}>
          {histories.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Keine Logbuch-Eintraege</div>
          ) : (
            <div style={{ position: "relative", paddingLeft: 24 }}>
              {/* vertikale Timeline-Linie */}
              <div style={{
                position: "absolute", left: 8, top: 6, bottom: 6,
                width: 2, background: "#e5e7eb",
              }} />
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {histories.map((h) => {
                  // Type-Code Mapping (heuristisch — die haeufigsten HERO codes):
                  //   1 = Status-Wechsel, 2 = Dokument, 3 = E-Mail, sonst = Notiz
                  const isStatus = (h.title || "").toLowerCase().startsWith("status:")
                  const isMail = (h.title || "").toLowerCase().startsWith("e-mail")
                  const isPayment = (h.title || "").toLowerCase().includes("zahlung")
                  const isDoc = (h.title || "").toLowerCase().match(/(angebot|rechnung).*erstellt/i)
                  const dotColor =
                    isStatus ? "#1d4ed8" :
                    isPayment ? "#16a34a" :
                    isMail ? "#7c3aed" :
                    isDoc ? "#ca8a04" : "#94a3b8"
                  return (
                    <li key={h.id} style={{ position: "relative", paddingBottom: 16, fontSize: 13 }}>
                      {/* Timeline-Punkt */}
                      <div style={{
                        position: "absolute", left: -22, top: 4,
                        width: 12, height: 12, borderRadius: "50%",
                        background: dotColor,
                        border: "3px solid #fff",
                        boxShadow: "0 0 0 1px " + dotColor,
                      }} />
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                        <div style={{ fontWeight: 600, color: "#1a1a2e" }}>
                          {h.title || "(ohne Titel)"}
                        </div>
                        <div style={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>
                          {formatDate(h.created)}{h.author && ` · ${h.author}`}
                        </div>
                      </div>
                      {h.text && (
                        <div style={{
                          color: "#475569", marginTop: 4,
                          whiteSpace: "pre-wrap", fontSize: 12,
                          lineHeight: 1.5,
                        }}>
                          {h.text}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </Section>
      </div>

      {/* HERO-Direktlink (Portal-Proxy fuer Auto-Cleanup) */}
      {!detail.data.partial && (
      <div style={{ marginTop: 24, textAlign: "center" }}>
        <a
          href={`/admin/open-hero/project/${p.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 16px", background: "#f1f5f9",
            color: "#475569", textDecoration: "none",
            borderRadius: 6, fontSize: 13,
          }}
        >
          In HERO öffnen <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <span style={{ minWidth: 100, color: "#94a3b8", fontSize: 12 }}>{label}</span>
      <span style={{ flex: 1 }}>{value}</span>
    </div>
  )
}
